import { Head } from "@inertiajs/react"
import { MailX } from "lucide-react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { MessageComposer } from "@/components/messaging/message-composer"
import {
  MessageThreadScroller,
  type MessageThreadScrollerHandle,
} from "@/components/messaging/message-thread-scroller"
import { Skeleton } from "@/components/ui/skeleton"
import { usePollOnFocus } from "@/hooks/use-poll-on-focus"
import AppLayout from "@/layouts/app-layout"
import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"
import { messagesPath } from "@/routes"
import type { CreatorMessagesPageProps } from "@/types/messaging"

/**
 * OKS-37 / OKS-654 — Creator-side single-thread surface.
 *
 * Render branches:
 *   - **Empty + can send (OKS-654):** "Message the brand" inline
 *     composer — a creator with an active / pending_charter membership
 *     can now START the conversation (reverses the OKS-37 reply-only
 *     rule). Driven by `canReply` (server widened its meaning to
 *     "can send"), NOT by thread existence.
 *   - **Empty + read-only:** illustration, no composer (paused /
 *     archived — the read path 404s before here in practice, kept as
 *     a defensive branch).
 *   - **Active thread:** `MessageScroller` list (OKS-654 migration) +
 *     reply composer.
 *
 * Mobile-first single column; max 640px on tablet/desktop. Polls
 * the thread + unread badge every 30s on focus.
 */
export default function CreatorMessagesIndex(props: CreatorMessagesPageProps) {
  const { t } = useTranslation()
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  // OKS-724 — held by the thread scroller; the composer forces the thread to
  // the newest message here on a successful own-send (null pre-thread, so the
  // handler is a safe no-op on the first send from the empty state).
  const scrollerRef = useRef<MessageThreadScrollerHandle>(null)

  usePollOnFocus({
    intervalMs: 30_000,
    only: ["conversation", "messages", "canReply", "unread_messages_count"],
    // OKS-819 — test-support-only seam for the poll-no-yank E2E.
    exposeTestReloadHandle: "__oksMessagesReload",
  })

  useEffect(() => {
    if (props.canReply) {
      composerRef.current?.focus({ preventScroll: true })
    }
  }, [props.canReply, props.conversation?.id])

  const hasThread = props.conversation !== null
  const isPaused = props.membership.status === "paused"
  const isArchived = props.membership.status === "archived"

  return (
    <AppLayout>
      <Head title={t("messaging.creator.page_title")} />
      <main
        // OKS-723 — fill the space the CreatorShell leaves below its chrome
        // instead of the old hard-coded `100vh-4rem` that ignored the header +
        // BrandStrip + banners and pushed the composer below the fold. The
        // shell is a flex column bounded to `100dvh − app-banner-stack`
        // (`creator-shell.tsx`), so `flex-1 min-h-0` here consumes exactly the
        // remaining height — header + strip + banners are absorbed by flexbox,
        // no viewport math and no measured var (so no first-paint settle race).
        //
        // CANONICAL BAND SHAPE (the OKS-723 fix): this column's direct children
        // are an ABSORBABLE header, ONE `flex-1 min-h-0` content band
        // (empty-state prompt OR thread OR skeleton), and a `shrink-0` composer
        // footer. The composer is a DIRECT sibling of the content band — NOT
        // nested inside a branch wrapper. An earlier iteration nested it one
        // level deep inside the empty-state's own `flex-1` column; on CI-Linux
        // (taller fallback-font chrome) that extra flex level let the composer
        // overflow its wrapper and land below the tab bar / fold. Hoisting it to
        // a direct `shrink-0` child pins it to this column's bottom.
        //
        // The composer's floor is the sum of everything ABOVE it that cannot
        // shrink. The shell banners (analytics + email) are shell-global chrome
        // we must show — on CI-Debian-bookworm they render ~60px taller than
        // macOS/jammy, which alone squeezes the column. So the ONE piece of
        // above-composer chrome this page owns — this header — must ALSO be
        // absorbable, else the fixed header + composer overflow the squeezed
        // column and drop the composer below the fold (the `:249` unverified +
        // email-banner red at 605 on CI-bookworm). `min-h-0 overflow-hidden`
        // (no `shrink-0`) lets the header collapse (clipping title-last) when —
        // and only when — the banners leave no room; in every normal case it
        // shows full. With the header absorbable, the composer floor is just
        // `shell-chrome + composer` (~522 on bookworm ≤ 568) — font-metric-
        // independent by construction, not tuned to any one distro's metrics.
        className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col"
        data-testid="messaging-creator-page"
      >
        {/*
            OKS-945 levers C + C' — 24px of the 90px this ticket reclaims, and
            the page title deliberately SURVIVES both of them.

            C (16px): the brand sub-line is gone. It rendered
            "Messages · {brand}", which duplicates the shell-global
            <BrandStrip> about 40px above it — the same name twice inside one
            phone-height column. Its locale key went with it rather than being
            left orphaned in the yml.

            C' (8px): `max-md:p-2`. The same "desktop padding surviving into a
            phone layout" that OKS-874 reclaimed on the composer, on the same
            argument. `p-3` is still declared, so md+ is unchanged.

            ⚠️ These 24px are the WEAKEST-EVIDENCED of the four levers, and
            that is worth knowing when run 3's integer comes back. The header
            was measured at 65px on CI, but never PARTITIONED there; 65 =
            border 1 + p-3 24 + h1 line 24 + sub-line 16 is arithmetic that
            reproduces the measured total, which a compensating pair of errors
            would do equally well. Contrast lever A, which subtracts two
            metric-independent tokens from a measured composer.

            The `min-h-0 overflow-hidden` stays exactly as OKS-723 left it. It
            is a CLIPPING GUARD for the overflow case, not a source of slack:
            the band is `flex: 1 1 0%` growing into positive free space, so
            flex-shrink never engages on this header while free space >= 0.
            Run 30680448579 confirmed that holds in the TYPED state too, not
            just at rest — with two lines typed the band floored at 0px on both
            branches, which is the band paying first and the header not
            shrinking. Had the header absorbed instead, the typed band would
            have measured nonzero and the whole
            `budget − header − composer` model would have been wrong.
        */}
        <header className="border-border min-h-0 overflow-hidden border-b p-3 max-md:p-2">
          <h1 className="text-base font-semibold">
            {t("messaging.creator.page_title")}
          </h1>
        </header>

        {/* Middle band — the ONE `flex-1 min-h-0` region that absorbs all
            vertical slack so the `shrink-0` composer below stays pinned. */}
        {/*
            🔴 OKS-945 — TWO OF THE FOUR BRANCHES BELOW ARE UNREACHABLE, AND
            THEY ARE DELIBERATELY LEFT UNFIXED. Recorded here so the next
            reader does not re-derive it, and does not "fix" them either.

            Both were nominated for a `min-h-0` fix on structure alone, and
            both were then checked against server code by PO and LQE
            independently:

            1. `<CreatorEmptyState>` (the `!props.canReply` leg).
               `CreatorMessagingController#load_membership` filters
               `CrewMembership` on `ConversationPolicy::CREATOR_ACCESSIBLE_STATUSES`
               (= %w[active pending_charter]) and raises RecordNotFound when
               nil. `props.membership.status` is serialised FROM that
               already-filtered record, so `isPaused` / `isArchived` are always
               false and `canReply` is always true for the same reason — this
               branch's own render condition can never hold. The controller
               says so in its own comment at `can_reply?`, and
               `spec/requests/messaging_security_spec.rb` asserts a paused
               creator gets 404 before render.

            2. The skeleton leg (`hasThread && props.messages.length === 0`).
               `Conversations::CreateMessage` creates the conversation and its
               first message inside ONE transaction, so a rolled-back message
               takes the conversation with it, and V1 has no message-delete
               route or destroy path. A conversation always has >= 1 message.

            WHY NO CODE CHANGE: a fix to dead code is unverifiable, and it
            manufactures exactly the appearance of coverage this ticket exists
            to remove.

            ⚠️ AND WHY NO SPEC LEG EITHER, which is the sharper trap: the
            testids are SHARED across branch pairs —
            `messaging-creator-empty-state` covers branches 1 and 2,
            `messaging-creator-thread` covers 3 and 4. A leg written for an
            unreachable branch lands silently on its reachable sibling, which
            HAS `min-h-0`, and reads green while asserting nothing whatsoever
            about the branch it names. That is worse than no leg.

            ⚠️ The axis is inverted on them anyway. Missing `min-h-0` means
            `min-height: auto`, so the item refuses to shrink below its content
            and OVERFLOWS rather than collapsing — it measures LARGE when
            broken. A band-height FLOOR assertion would pass identically before
            and after any fix. (The same inversion is live and REACHABLE on the
            brand side, `components/messaging/thread-pane.tsx`: whatever fixes
            it has to be verified with a not-clipped assertion rather than a
            height floor, for exactly this reason.)
        */}
        {!hasThread ? (
          props.canReply ? (
            <CreatorInitiateEmptyState brandName={props.brandName ?? null} />
          ) : (
            <CreatorEmptyState
              paused={isPaused || isArchived}
              brandName={props.brandName ?? null}
            />
          )
        ) : (
          <>
            {(isPaused || isArchived) && (
              <div
                className="border-border bg-muted text-muted-foreground shrink-0 border-b px-3 py-2 text-xs"
                role="status"
                data-testid="messaging-creator-paused-banner"
              >
                {t("messaging.creator.paused_description")}
              </div>
            )}
            {props.messages.length === 0 ? (
              <div
                role="log"
                aria-live="polite"
                className="min-h-0 flex-1 overflow-y-auto p-3"
                data-testid="messaging-creator-thread"
              >
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div className="min-h-0 flex-1">
                <MessageThreadScroller
                  ref={scrollerRef}
                  messages={props.messages}
                  ariaLabel={t("messaging.creator.page_title")}
                  testId="messaging-creator-thread"
                />
              </div>
            )}
          </>
        )}

        {/* Composer footer — a single `shrink-0` direct child, rendered for
            both the initiate-empty and active-thread branches whenever the
            creator can send. `conversationId` is null pre-thread (the
            controller lazy-creates on first send). */}
        {props.canReply && (
          <MessageComposer
            ref={composerRef}
            actionUrl={messagesPath()}
            limit={props.composerLimit}
            conversationId={props.conversation?.id ?? null}
            onSuccess={() => scrollerRef.current?.scrollToNewest()}
          />
        )}
      </main>
    </AppLayout>
  )
}

/**
 * OKS-654 — Creator-initiate empty state. Shown when the creator has
 * no thread yet but their membership lets them send (active /
 * pending_charter). A "Message the brand" prompt; the shared
 * `MessageComposer` footer (hoisted to the page column, POSTing to
 * `messagesPath()`) lets the creator start the conversation — the
 * controller lazy-creates it on first send.
 *
 * OKS-723 — this is the middle band of the page's three-band flex column:
 * `flex-1 min-h-0 overflow-y-auto` so it absorbs ALL vertical slack and,
 * when chrome is tall (CI-Linux fallback-font metrics wrap the analytics +
 * email banners ~80px taller), it shrinks/scrolls INSTEAD of pushing the
 * `shrink-0` composer footer below the fold.
 *
 * CRITICAL: the scrollable band carries NO padding of its own — the `p-6`
 * lives on the INNER wrapper. A flex item's `min-height:0` lets it shrink
 * below its CONTENT, but padding on the scroll container itself acts as an
 * irreducible floor (measured: the band refused to collapse below its 48px
 * `p-6` box on CI-Linux, overflowing the composer ~20px past the tab bar /
 * fold). Padding-free, the band collapses toward 0 and the inner prompt
 * scrolls — the same pattern the thread scroller uses (padding on
 * `MessageScrollerContent`, not on the `flex-1 min-h-0` band).
 */
function CreatorInitiateEmptyState({
  brandName,
}: {
  brandName: string | null
}) {
  const { t } = useTranslation()
  const description = brandName
    ? t("messaging.creator.initiate_description", { brand_name: brandName })
    : t("messaging.creator.initiate_description_no_brand")

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      data-testid="messaging-creator-empty-state"
    >
      <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
        <p className="text-sm font-medium">
          {t("messaging.creator.initiate_title")}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">{description}</p>
      </div>
    </div>
  )
}

/**
 * OKS-37 / OKS-405 — Creator messaging read-only empty state.
 *
 * Renders the shared `EmptyState` primitive (OKS-405 converged the four
 * creator empty surfaces onto it); `data-testid` is preserved. No CTA —
 * the creator is on a paused / archived membership (OKS-654 lets active
 * memberships initiate via `CreatorInitiateEmptyState` instead). Plays
 * `overlay-enter` once on mount (motion-safe).
 */
function CreatorEmptyState({
  paused,
  brandName,
}: {
  paused: boolean
  brandName: string | null
}) {
  const { t } = useTranslation()
  // Direct `t("literal-key")` calls — the inertia_i18n scanner only
  // matches static-string args. The brand vs no-brand / paused branches
  // select WHICH `t(...)` runs; the key arg stays a literal so the
  // unused-keys checker still sees every key.
  const title = paused
    ? t("messaging.creator.paused_title")
    : t("messaging.creator.empty_title")
  const description = paused
    ? t("messaging.creator.paused_description")
    : brandName
      ? t("messaging.creator.empty_description", { brand_name: brandName })
      : t("messaging.creator.empty_description_no_brand")
  return (
    <EmptyState
      icon={MailX}
      title={title}
      description={description}
      data-testid="messaging-creator-empty-state"
      className={cn("flex-1 justify-center", MOTION_BEATS["overlay-enter"])}
    />
  )
}
